import { CellRuleValue, MinimalCellRule } from 'interfaces/cellRule';
import { MultiplexPanelView } from 'interfaces/multiplexPanels';
import { MultiplexPanelCellRules } from 'interfaces/study';
import { panelCellRuleMarkerIds } from './cellRuleRevisions';

export interface VisibleStudyPanelCellRules {
  key: string;
  panelId: string;
  panelName: string;
  revision?: number;
  savedAt?: string;
  columns: string[];
  displayNames: Record<string, string>;
  cellTypeIds: string[];
  cellTypeDisplayNames: Record<string, string>;
  captions: Record<string, string>;
  rules: MinimalCellRule[];
  hasRules: boolean;
}

const trimmed = (value?: string | null) => value?.trim() || '';

export const projectStudyPanelCellRules = (
  entries: MultiplexPanelCellRules[] | null | undefined,
  panels: MultiplexPanelView[] | null | undefined,
  displayNameForColumn: (columnId: string) => string,
  displayNameForCellType: (cellTypeId: string) => string
): VisibleStudyPanelCellRules[] =>
  (entries ?? []).map((entry, entryIndex) => {
    const panel = (panels ?? []).find((candidate) => candidate.panelId === entry?.panelId);
    const panelMarkerIds = panel ? panelCellRuleMarkerIds(panel) : entry?.markerIds ?? [];
    const panelMarkerSet = new Set(panelMarkerIds);
    const channelNames = Object.fromEntries(
      (panel?.channels ?? []).flatMap((channel) => {
        const analysisName = channel.saved?.analysisName;
        const displayName = trimmed(channel.saved?.displayName);
        return analysisName && displayName && channel.saved?.channelModality !== 'dna'
          ? [[analysisName, displayName]]
          : [];
      })
    );
    const ruleList = Array.isArray(entry?.rules) ? entry.rules : [];
    const ruleKeys: string[] = [];
    ruleList.forEach((rule) => {
      Object.keys(rule?.rules ?? {}).forEach((key) => {
        if (key && !ruleKeys.includes(key)) ruleKeys.push(key);
      });
    });
    const outsidePanel = panelMarkerSet.size ? ruleKeys.filter((columnId) => !panelMarkerSet.has(columnId)) : [];
    const columns = panelMarkerIds.length ? [...panelMarkerIds, ...outsidePanel] : ruleKeys;
    const seenTargets = new Map<string, number>();
    const cellTypeIds: string[] = [];
    const cellTypeDisplayNames: Record<string, string> = {};
    const captions: Record<string, string> = {};
    const rules: MinimalCellRule[] = [];

    ruleList.forEach((rule, ruleIndex) => {
      const targetPath = rule?.targetCellTypePath || `missing-cell-type-${entryIndex}-${ruleIndex}`;
      const count = seenTargets.get(targetPath) ?? 0;
      seenTargets.set(targetPath, count + 1);
      const rowId = count === 0 ? targetPath : `${targetPath}#${count}`;
      cellTypeIds.push(rowId);
      const targetName = displayNameForCellType(targetPath);
      if (targetName !== targetPath || rowId !== targetPath) cellTypeDisplayNames[rowId] = targetName;
      const notes = [
        count > 0 ? 'Duplicate' : null,
        rule?.sourceCellTypePath ? `From ${displayNameForCellType(rule.sourceCellTypePath)}` : null,
      ].filter((note): note is string => Boolean(note));
      if (notes.length > 0) captions[rowId] = notes.join(' · ');
      Object.entries(rule?.rules ?? {}).forEach(([stainTypeId, ruleValue]) => {
        rules.push({ cellTypeId: rowId, stainTypeId, ruleValue: ruleValue as CellRuleValue });
      });
    });

    return {
      key: `${entry?.panelId ?? 'missing-panel'}-${entry?.savedAt ?? entryIndex}-${entryIndex}`,
      panelId: entry?.panelId ?? '',
      revision: entry?.revision,
      panelName: trimmed(panel?.name) || entry?.panelId || 'Saved panel',
      savedAt: entry?.savedAt,
      columns,
      displayNames: Object.fromEntries(
        columns.map((columnId) => {
          const resolved = displayNameForColumn(columnId);
          const name = resolved && resolved !== columnId ? resolved : channelNames[columnId] || resolved || columnId;
          return [columnId, outsidePanel.includes(columnId) ? `${name} (not on this panel)` : name];
        })
      ),
      cellTypeIds,
      cellTypeDisplayNames,
      captions,
      rules,
      hasRules: ruleList.length > 0,
    };
  });
